import { useState, useEffect, useCallback } from 'react';
import {
  RawManufacturingUnit,
  NormalizedManufacturingUnit,
  ManufacturingMapStatistics,
  INDIA_CENTER,
} from '../types/gaushalaMap.types';
import { getLocationCoordinates } from '../services/geocodingService';

const MFG_CACHE_KEY = 'map_mfg_units_cache';
const MAP_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function readMfgCache(): NormalizedManufacturingUnit[] | null {
  try {
    const raw = localStorage.getItem(MFG_CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > MAP_CACHE_TTL) return null;
    return data as NormalizedManufacturingUnit[];
  } catch { return null; }
}

function writeMfgCache(data: NormalizedManufacturingUnit[]) {
  try { localStorage.setItem(MFG_CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

// Use commerce service endpoint for manufacturing units
// Use relative path in dev mode for Vite proxy
const isDevelopment = import.meta.env.DEV;
const COMMERCE_BASE = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');
const COMMERCE_API_BASE = `${COMMERCE_BASE}/api/v1`;

const getAuthHeader = () => {
  const token = localStorage.getItem('saubhagya_jwt_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

/**
 * Custom Hook: useManufacturingMap
 *
 * Fetches and normalizes manufacturing unit data for map display
 * Similar to useGaushalaMap but for manufacturing units
 */

interface UseManufacturingMapReturn {
  units: NormalizedManufacturingUnit[];
  isLoading: boolean;
  error: string | null;
  statistics: ManufacturingMapStatistics;
  refetch: () => void;
}

export const useManufacturingMap = (): UseManufacturingMapReturn => {
  const cachedUnits = readMfgCache();
  const [units, setUnits] = useState<NormalizedManufacturingUnit[]>(cachedUnits ?? []);
  const [isLoading, setIsLoading] = useState(cachedUnits === null); // false if cache hit
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<ManufacturingMapStatistics>({
    total: cachedUnits?.length ?? 0,
    fromDatabase: 0,
    geocoded: 0,
  });

  /**
   * Normalize a raw manufacturing unit to have guaranteed coordinates
   */
  const normalizeUnit = (unit: RawManufacturingUnit): NormalizedManufacturingUnit | null => {
    // Check if unit has valid coordinates from database
    const hasDbCoords =
      unit.latitude !== null &&
      unit.latitude !== undefined &&
      unit.longitude !== null &&
      unit.longitude !== undefined &&
      !isNaN(unit.latitude) &&
      !isNaN(unit.longitude);

    if (hasDbCoords) {
      console.log(`[ManufacturingMap] Unit "${unit.name}" has DB coordinates: ${unit.latitude}, ${unit.longitude}`);
      return {
        id: unit.id,
        name: unit.name,
        unitCode: unit.unitCode || `UNIT-${unit.id}`,
        address: unit.location || unit.address || 'Unknown location',
        latitude: unit.latitude!,
        longitude: unit.longitude!,
        dataSource: 'database',
        confidence: 'high',
        metadata: {
          capacity: unit.capacity,
          status: unit.status,
          createdAt: unit.createdAt,
          location: unit.location,
        },
      };
    }

    // Try to geocode from location/address
    const addressToGeocode = unit.location || unit.address;
    if (addressToGeocode) {
      const coords = getLocationCoordinates(addressToGeocode);

      // Check if we got valid coordinates (not India center)
      const isIndiaCenter =
        Math.abs(coords.latitude - INDIA_CENTER.latitude) < 0.01 &&
        Math.abs(coords.longitude - INDIA_CENTER.longitude) < 0.01;

      if (!isIndiaCenter) {
        console.log(`[ManufacturingMap] Unit "${unit.name}" geocoded from address: ${coords.latitude}, ${coords.longitude}`);
        return {
          id: unit.id,
          name: unit.name,
          unitCode: unit.unitCode || `UNIT-${unit.id}`,
          address: addressToGeocode,
          latitude: coords.latitude,
          longitude: coords.longitude,
          dataSource: 'geocoded_from_address',
          confidence: 'medium',
          metadata: {
            capacity: unit.capacity,
            status: unit.status,
            createdAt: unit.createdAt,
            location: unit.location,
          },
        };
      }
    }

    // Skip units without valid location
    console.log(`[ManufacturingMap] Skipping unit "${unit.name}" - no valid location`);
    return null;
  };

  /**
   * Fetch and process manufacturing units from commerce service
   */
  const fetchUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[useManufacturingMap] Fetching manufacturing units from commerce service...');

      // Call commerce service endpoint for manufacturing units
      const response = await fetch(`${COMMERCE_API_BASE}/mfg-production-batches/manufacturing-units`, {
        method: 'GET',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error(data.message || 'Failed to fetch manufacturing units');
      }

      const rawUnits: RawManufacturingUnit[] = data.data;
      console.log(`[useManufacturingMap] Fetched ${rawUnits.length} manufacturing units`);

      // Normalize units
      const normalizedUnits: NormalizedManufacturingUnit[] = [];
      let fromDatabase = 0;
      let geocoded = 0;

      for (const unit of rawUnits) {
        const normalized = normalizeUnit(unit);
        if (normalized) {
          normalizedUnits.push(normalized);
          if (normalized.dataSource === 'database') {
            fromDatabase++;
          } else {
            geocoded++;
          }
        }
      }

      console.log(`[useManufacturingMap] Normalized ${normalizedUnits.length} units (${fromDatabase} from DB, ${geocoded} geocoded)`);

      writeMfgCache(normalizedUnits);
      setUnits(normalizedUnits);
      setStatistics({
        total: normalizedUnits.length,
        fromDatabase,
        geocoded,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useManufacturingMap] Error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  return {
    units,
    isLoading,
    error,
    statistics,
    refetch: fetchUnits,
  };
};

export default useManufacturingMap;
